import { useEffect, useState, useRef, Fragment } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { CONFIG_BY_VARIANT, LF_CONFIG } from "@/pages/estimatorConfig";
import CrudManager from "@/components/CrudManager";
import NestingCanvas from "@/components/NestingCanvas";
import { buildThumbsByKey, renderMockupBlob } from "@/lib/sheetMockup";
import { uploadArtBlob, artUrl } from "@/lib/uploadArt";
import { Metric, EmptyState, SectionLabel, PricingPanel, useRushRates } from "@/components/Metric";
import LFPriceIntel from "@/components/LFPriceIntel";
import LFPdfImport from "@/components/LFPdfImport";
import LFImageImport from "@/components/LFImageImport";
import CutFileDialog from "@/components/CutFileDialog";
import BatchCutDialog from "@/components/BatchCutDialog";
import { HelpHint } from "@/components/HelpHint";
import { SaveQuoteBar } from "@/components/SaveQuote";
import VolumePricingTable from "@/components/VolumePricingTable";
import { InkPicker } from "@/components/InkPicker";
import { useRequote } from "@/lib/useRequote";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, num } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X, Calculator, Layers, Ruler, Tag, DollarSign, Info, RotateCw, Check, AlertTriangle, ClipboardList, Printer, Link2, Link2Off, Scissors } from "lucide-react";

const MODES = [
  { v: "print", l: "Print Only" },
  { v: "diecut", l: "Die-Cut Only" },
  { v: "print_cut", l: "Print + Cut" },
];

import { MiscRows, useMiscOptions } from "@/components/MiscPicker";
import { ServicesPicker } from "@/components/ServicesPicker";
import { MiscListTab } from "@/components/MiscListTab";
const LF_SUBTYPES = {
  poster: { label: "Poster Paper", color: "#2563EB", order: 1 },
  vinyl: { label: "Vinyl", color: "#16A34A", order: 2 },
  banner: { label: "Banner", color: "#EA580C", order: 3 },
  tshirt: { label: "Tshirt Vinyl", color: "#0D9488", order: 4 },
  transfer: { label: "Transfer Tape", color: "#64748B", order: 5 },
  lamination: { label: "Lamination", color: "#7C3AED", order: 6 },
  special: { label: "Special Vinyls", color: "#DB2777", order: 7 },
  decoration: { label: "Decoration Vinyl", color: "#F59E0B", order: 8 },
};
const TYPE_TO_SUBTYPE = { vinyl: "vinyl", banner: "banner", paper: "poster", specialty: "special" };
// Finishing options available per material subtype in Large Format.
const FINISH_BY_SUBTYPE = {
  poster: ["laminate", "decoration"],
  banner: ["reinforced", "grommets"],
  vinyl: ["diecut", "transfer_tape", "laminate", "decoration"],
  special: ["diecut", "transfer_tape", "laminate", "decoration"],
};
const GROMMET_COUNTS = { top_center: 1, top_corners: 2, "4_corners": 4, "6_grommets": 6 };
const subtypeKey = (it) => {
  const n = (it.name || "").toLowerCase();
  // Name signals win over a generic lf_subtype so Poster/Banner/Lamination rolls
  // classify correctly even when their stored lf_subtype defaulted to "vinyl".
  if (/decor/.test(n)) return "decoration";
  if (/lamin/.test(n)) return "lamination";
  if (/transfer tape|app(lication)? tape/.test(n)) return "transfer";
  if (/banner|mesh/.test(n)) return "banner";
  if (/poster|bond paper|\bpaper\b/.test(n)) return "poster";
  if (/tshirt|t-shirt|\bhtv\b|heat transfer/.test(n)) return "tshirt";
  if (/reversible|luster|special/.test(n)) return "special";
  if (it.lf_subtype) return it.lf_subtype;
  return TYPE_TO_SUBTYPE[it.material_type] || "vinyl";
};
const leadNum = (s) => { const m = (s || "").match(/^\s*#?\s*(\d+)/); return m ? +m[1] : 9999; };
// VersaWorks-style material family: same product across widths. Uses explicit m.family, else derives
// a base name by stripping the leading "#nn", the "(CODE)" and any trailing/embedded width like 54".
const familyName = (m) => {
  if (m.family) return m.family;
  let n = (m.name || "").replace(/^\s*#?\s*\d+\s*/, "").replace(/\([^)]*\)/g, "");
  n = n.replace(/[-–]?\s*\d+(?:\.\d+)?\s*["”“'']\s*$/, "");
  n = n.replace(/\b\d+(?:\.\d+)?\s*["”“'']/g, "");
  return n.replace(/\s{2,}/g, " ").trim() || (m.name || "");
};
const rollSort = (a, b) => ((LF_SUBTYPES[subtypeKey(a)]?.order || 99) - (LF_SUBTYPES[subtypeKey(b)]?.order || 99)) || (leadNum(a.name) - leadNum(b.name)) || (a.name || "").localeCompare(b.name || "");

const matFields = [
  { name: "name", label: "Name", type: "text", full: true },
  { name: "code", label: "Code", type: "text" },
  { name: "material_type", label: "Type", type: "select", options: ["vinyl", "banner", "specialty", "paper"], default: "vinyl" },
  { name: "roll_width", label: "Roll Width (in)", type: "number", default: 54 },
  { name: "printable_width", label: "Printable Width (in)", type: "number", default: 52 },
  { name: "price_per_sqft", label: "Price / sqft (CAD)", type: "number", default: 0.85 },
  { name: "min_linear_feet", label: "Min Linear Feet", type: "number", default: 1 },
  { name: "sticker_compatible", label: "Sticker Compatible", type: "switch" },
  { name: "linked_material_id", label: "Link to Materials DB (cost from purchases)", type: "material-link", full: true },
];
const matCols = [
  { name: "name", label: "Name" },
  { name: "code", label: "Code", mono: true },
  { name: "lf_subtype", label: "Subtype", render: (i) => { const s = LF_SUBTYPES[subtypeKey(i)]; return <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: s.color }} data-testid="roll-subtype-chip">{s.label}</span>; } },
  { name: "printable_width", label: "Printable", mono: true, render: (i) => `${num(i.printable_width, 0)}"` },
  { name: "price_per_sqft", label: "$/sqft", mono: true, render: (i) => money(i.price_per_sqft) },
  { name: "linked", label: "Linked", render: (i) => i.linked_material_name || "—" },
  { name: "sticker_compatible", label: "Sticker", render: (i) => (i.sticker_compatible ? "Yes" : "—") },
];
const presetFields = [
  { name: "name", label: "Name", type: "text", full: true },
  { name: "width", label: "Width (in)", type: "number", default: 24 },
  { name: "height", label: "Height (in)", type: "number", default: 18 },
];
const presetCols = [
  { name: "name", label: "Name" },
  { name: "width", label: "W", mono: true, render: (i) => `${num(i.width, 0)}"` },
  { name: "height", label: "H", mono: true, render: (i) => `${num(i.height, 0)}"` },
];

const sell = (t) => t?.selling_price ?? t?.wholesale_price;

// Miscellaneous quick-list grouping for the Large Format tab.
const LF_MISC_GROUPS = [
  { label: "Vinyl", match: ["vinyl", "special", "decoration", "lamination", "transfer", "tshirt"] },
  { label: "Banner / Poster", match: ["banner", "poster"] },
];

export default function LargeFormat({ variant = "lf", config }) {
  const cfg = config || CONFIG_BY_VARIANT[variant] || LF_CONFIG;
  const isSticker = cfg.sticker;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sizes, setSizes] = useState(cfg.defaultSizes.map((s) => ({ ...s })));
  const [pageThumbs, setPageThumbs] = useState({});
  const [zoom, setZoom] = useState(null);
  const [cutRow, setCutRow] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [mode, setMode] = useState("print");
  const [laminate, setLaminate] = useState(false);
  const [transferTape, setTransferTape] = useState(false);
  const [extras, setExtras] = useState([]);
  const [machineId, setMachineId] = useState("none");
  const [services, setServices] = useState(null);
  const [inkCoverage, setInkCoverage] = useState(100);
  const [presets, setPresets] = useState([]);
  const [orderNo, setOrderNo] = useState("");
  const [orderName, setOrderName] = useState("");
  const [quoteStamp, setQuoteStamp] = useState("");
  const [designs, setDesigns] = useState(1);
  const [batchDim, setBatchDim] = useState("height");
  const [batchVal, setBatchVal] = useState("");
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState(null);
  const selRef = useRef(null);
  const [materialId, setMaterialId] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const miscOpts = useMiscOptions(isSticker ? "stickers" : "large-format", isSticker ? undefined : typeKey);
  const imposeRef = useRef(null);
  const _dl = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); };
  const _body = () => ({ sizes: sizes.map((s) => ({ width: +s.width, height: +s.height, qty: +s.qty, name: s.name || "", page: (s.page != null ? s.page : null), diecut: !!s.diecut && finishOpts.includes("diecut"), transfer_tape: !!s.transfer_tape && finishOpts.includes("transfer_tape"), laminate: !!s.laminate && finishOpts.includes("laminate"), decoration: !!s.decoration && finishOpts.includes("decoration"), reinforced_edges: !!s.reinforced_edges && finishOpts.includes("reinforced"), grommets: !!s.grommets && finishOpts.includes("grommets"), grommet_mode: s.grommet_mode || "", laminate_material_id: s.laminate_material_id || "", decoration_material_id: s.decoration_material_id || "", misc: (s.misc || []).filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty })) })).filter((s) => +s.width > 0 && +s.height > 0), mode, laminate, transfer_tape: transferTape, extras: extras.map((e) => ({ type: e.type, material_id: e.material_id, width: +e.width || 0, height: +e.height || 0, qty: +e.qty || 1, die_cut: !!e.die_cut, transfer_tape: !!e.transfer_tape })), material_ids: typeMats.map((m) => m.id), machine_id: machineId !== "none" ? machineId : null, sticker: isSticker, designs: +designs || 1, ink_coverage_pct: inkCoverage, services });
  const dlProd = async () => { try { const res = await api.post(`/lf/production-sheet?material_id=${sel.material.id}`, _body(), { responseType: "blob" }); _dl(res.data, "production-sheet.pdf"); } catch (e) { toast.error("Could not generate production sheet"); } };
  const dlImpose = async (ev) => { const f = ev.target.files?.[0]; if (!f) return; try { toast.info("Building nested PDF…"); const fd = new FormData(); fd.append("file", f); fd.append("payload", JSON.stringify(_body())); fd.append("material_id", sel.material.id); const res = await api.post("/lf/impose", fd, { responseType: "blob", headers: { "Content-Type": "multipart/form-data" } }); _dl(res.data, "nested-print.pdf"); } catch (e) { toast.error("Could not impose the PDF"); } ev.target.value = ""; };
  const [rushInfo, setRushInfo] = useState({ pct: 0, factor: 1, label: "Standard" });
  const rates = useRushRates();
  const retailTaxF = 1 + ((rates.gst || 0) + (rates.pst || 0)) / 100;
  const wsTaxF = 1 + (rates.gst || 0) / 100;

  const [rollMats, setRollMats] = useState([]);
  const [familyKey, setFamilyKey] = useState("");
  const [nestVariants, setNestVariants] = useState(null);  const [nestIdx, setNestIdx] = useState(0);
  const [nestSpacing, setNestSpacing] = useState(0);
  const [nestFit, setNestFit] = useState(false);
  const fetchVariants = async () => {
    if (!sel) return;
    try {
      const { data } = await api.post("/lf/nest-variants", {
        sizes: sizes.map((s) => ({ width: +s.width, height: +s.height, qty: +s.qty, name: s.name || "", page: (s.page != null ? s.page : null) })),
        printable_width: sel.material.printable_width, spacing: +nestSpacing || 0, sticker: isSticker, designs: +designs || 1 });
      if (!data.variants?.length) { setNestVariants(null); return; }
      setNestVariants(data.variants); setNestIdx((i) => Math.min(i, data.variants.length - 1));
    } catch (e) { toast.error("Could not rearrange tiles"); }
  };
  const rearrange = async () => {
    if (nestVariants && nestVariants.length) { setNestIdx((i) => (i + 1) % nestVariants.length); return; }
    await fetchVariants();
  };
  // Media consumption vs. what's on the shelf for the selected roll.
  const jobLinearFt = () => {
    if (nestVariants && nestVariants.length) return nestVariants[nestIdx].linear_feet || 0;
    const Ls = sel?.layouts || (sel?.layout ? [sel.layout] : []);
    return Ls.reduce((a, L) => a + (L.used_length || 0), 0) / 12;
  };
  const availLinearFt = () => {
    const rw = sel?.material?.roll_width || 0;
    const stock = sel?.material?.stock_qty || 0;
    return rw ? stock / (rw / 12) : 0;
  };
  useEffect(() => { api.get("/size-presets").then((r) => setPresets(r.data)); }, []);
  useEffect(() => { if (isAdmin) api.get("/roll-materials", { params: { module: isSticker ? "stickers" : "large-format" } }).then((r) => setRollMats(r.data)).catch(() => {}); }, [isAdmin, isSticker]);
  const [lfSet, setLfSet] = useState({});
  useEffect(() => { if (isAdmin) api.get("/settings").then((r) => setLfSet(r.data || {})).catch(() => {}); }, [isAdmin]);

  const addRow = () => sizes.length < 25 && setSizes([...sizes, { width: 12, height: 12, qty: 1 }]);
  const rmRow = (i) => setSizes(sizes.filter((_, idx) => idx !== i));
  const upd = (i, k, v) => setSizes(sizes.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  // Ratio-linked dimension edit: when a row's aspect is locked, editing one dimension fills the other.
  const updDim = (i, key, v) => setSizes(sizes.map((s, idx) => {
    if (idx !== i) return s;
    const ns = { ...s, [key]: v };
    const rw = +ns.rw, rh = +ns.rh;
    if (ns.ratioLocked && rw > 0 && rh > 0 && +v > 0) {
      const r = rw / rh;
      if (key === "width") ns.height = Math.round((+v / r) * 100) / 100;
      else if (key === "height") ns.width = Math.round((+v * r) * 100) / 100;
    }
    return ns;
  }));
  const updRatio = (i, key, v) => setSizes(sizes.map((s, idx) => {
    if (idx !== i) return s;
    const ns = { ...s, [key]: v };
    const rw = +ns.rw, rh = +ns.rh;
    if (ns.ratioLocked && rw > 0 && rh > 0 && +ns.width > 0) ns.height = Math.round((+ns.width / (rw / rh)) * 100) / 100;
    return ns;
  }));
  const toggleLock = (i) => setSizes(sizes.map((s, idx) => {
    if (idx !== i) return s;
    const on = !s.ratioLocked; const ns = { ...s, ratioLocked: on };
    if (on) {
      // Capture the current WIDTH×HEIGHT as the locked proportion (like Photoshop's chain link),
      // so editing either side immediately scales the other with no extra step.
      if (!(+ns.rw > 0 && +ns.rh > 0) && +ns.width > 0 && +ns.height > 0) {
        ns.rw = +ns.width; ns.rh = +ns.height;
      }
      const rw = +ns.rw, rh = +ns.rh;
      if (rw > 0 && rh > 0 && +ns.width > 0) ns.height = Math.round((+ns.width / (rw / rh)) * 100) / 100;
    }
    return ns;
  }));
  // Bulk artwork import → one locked-ratio size row per image, thumbnails stored in pageThumbs.
  const applyImages = (imgRows, replace) => {
    const prev = replace ? {} : { ...pageThumbs };
    const keys = Object.keys(prev).map(Number);
    let idx = keys.length ? Math.max(...keys) + 1 : 0;
    const nextThumbs = { ...prev };
    const newRows = imgRows.map((r) => { const page = idx++; nextThumbs[page] = r.thumb; return { width: "", height: "", qty: 1, name: r.name, page, rw: r.rw, rh: r.rh, ratioLocked: true, art_id: r.art_id || null }; });
    setPageThumbs(nextThumbs);
    setSizes(replace ? newRows.slice(0, 25) : [...sizes, ...newRows].slice(0, 25));
  };
  // Apply one dimension to every row at once; rows with a locked ratio fill the other side automatically.
  const applyBatchDim = () => {
    const v = +batchVal;
    if (!(v > 0)) return;
    const other = batchDim === "height" ? "width" : "height";
    setSizes(sizes.map((s) => {
      const ns = { ...s, [batchDim]: v };
      const rw = +ns.rw, rh = +ns.rh;
      if (rw > 0 && rh > 0) {
        const r = rw / rh;
        ns[other] = batchDim === "height" ? Math.round((v * r) * 100) / 100 : Math.round((v / r) * 100) / 100;
      }
      return ns;
    }));
  };
  const addPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (p && sizes.length < 25) setSizes([...sizes, { width: p.width, height: p.height, qty: 1 }]);
  };
  const fmtDim = (v) => (v == null ? "" : (Number.isInteger(v) ? v : Math.round(v * 10) / 10));
  // Large Format section summary (visual only — width = roll printable width, length = arranged pieces).
  const lfSecLine = (L, count = 1) => {
    if (!L || isSticker) return null;
    const w = Math.round(sel?.material?.printable_width || L.bin_width || 0);
    const len = Math.round(L.used_length || 0);
    const n = (L.placements || []).length;
    if (L.oversize) return `${fmtDim(L.real_w)}×${fmtDim(L.real_h)}" · scaled for print`;
    return `${w}×${len}" · ${n} pieces · ${count} section${count > 1 ? "s" : ""}`;
  };
  // Stickers only: per-ACTUAL-section breakdown (piece size in the section, count, full / add N more).
  const stickerFill = () => {
    if (!isSticker || !sel) return null;
    const V = (nestVariants && nestVariants.length) ? nestVariants[nestIdx] : null;
    const L = V || (sel.layouts || (sel.layout ? [sel.layout] : []))[0];
    if (!L || !L.placements || !L.placements.length) return null;
    const SH = L.sheet_height || 38;
    const SW = Math.min(sel.material?.printable_width || 52, 52);
    const g = Number(nestSpacing) > 0 ? Number(nestSpacing) : 0.125;
    const capFor = (w, h) => {
      const cols = (a) => Math.floor((SW + g) / (a + g));
      const rowsF = (a) => Math.floor((SH + g) / (a + g));
      return Math.max(1, Math.max(cols(w) * rowsF(h), cols(h) * rowsF(w)));
    };
    const bySec = {};
    L.placements.forEach((p) => {
      const s = Math.floor((p.y + 0.001) / SH);
      (bySec[s] = bySec[s] || []).push(p);
    });
    const secIdx = Object.keys(bySec).map(Number).sort((a, b) => a - b);
    const caps = L.section_caps || {};
    const rows = secIdx.map((s, idx) => {
      const items = bySec[s];
      const counts = {};
      items.forEach((p) => { counts[p.label] = (counts[p.label] || 0) + 1; });
      const label = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
      const [w, h] = label.split("x").map(Number);
      const cap = caps[label] != null ? caps[label] : capFor(w, h);
      const count = items.length;
      const full = count >= cap;
      return { i: s + 1, dim: `${w}"×${h}"`, count, full, add: full ? 0 : cap - count, mixed: Object.keys(counts).length > 1 };
    });
    return { rows, secDim: `${SW}"×${SH}"` };
  };
  const pieceRows = () => {
    if (!sel) return [];
    const t = sel.total; const pw = sel.material.printable_width || 52; const rF = rushInfo.factor;
    const rows = (sel.sizes || []).map((r, i) => {
      const src = sizes[i] || {};
      const inb = r.width * r.height * r.qty;
      return { i, w: r.width, h: r.height, qty: r.qty, billed: inb / 144, diecut: !!src.diecut, transfer_tape: !!src.transfer_tape,
               tiled: !!r.tiled, panels: r.panels || 1, panel_w: r.panel_w, panel_h: r.panel_h };
    });
    const TB = rows.reduce((a, r) => a + r.billed, 0) || 1;
    const dcTot = t.diecut_cost || 0, tcTot = t.tape_cost || 0;
    const dcBilled = rows.filter((r) => r.diecut).reduce((a, r) => a + r.billed, 0) || 1;
    const tcBilled = rows.filter((r) => r.transfer_tape).reduce((a, r) => a + r.billed, 0) || 1;
    const base = (t.selling_price || 0) - dcTot - tcTot;
    const wsFactor = t.selling_price ? (t.wholesale_price || 0) / t.selling_price : 0;
    const costTot = t.base_cost || 0;
    return rows.map((r) => {
      const share = r.billed / TB;
      const ownDc = r.diecut ? dcTot * (r.billed / dcBilled) : 0;
      const ownTc = r.transfer_tape ? tcTot * (r.billed / tcBilled) : 0;
      const retail = (base * share + ownDc + ownTc) * rF;
      const ws = retail * wsFactor;
      const material = (t.material_cost || 0) * share;
      const cost = costTot * share * rF;
      return { ...r, retail, ws, material, cost, profit: retail - cost };
    });
  };

  const _capWord = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
  const prodTimeLabel = () => {
    const l = (rushInfo.label || "").toLowerCase();
    if (l.startsWith("same")) return "Same day";
    if (l.startsWith("next")) return "Next day";
    return "Standard";
  };
  const buildTicket = () => {
    if (!sel) return null;
    const lamCount = sizes.reduce((a, s) => a + (s.laminate ? (+s.qty || 0) : 0), 0);
    const total = sizes.reduce((a, s) => a + (+s.qty || 0), 0);
    const lamFinish = _capWord(defLam?.lam_finish || "");
    const lines = sizes.map((s, i) => {
      const ss = (sel.sizes || [])[i] || {};
      const lm = s.laminate ? (rollMats.find((m) => m.id === s.laminate_material_id) || defLam) : null;
      const dm = s.decoration ? (rollMats.find((m) => m.id === s.decoration_material_id) || defDeco) : null;
      const procs = [isSticker ? "Print & Cut" : "Print"];
      if (s.laminate) procs.push(isSticker ? `Lam${lm?.name ? ` (${lm.name})` : ""}` : `Lam${lm?.lam_finish ? ` (${_capWord(lm.lam_finish)})` : ""}`);
      if (s.diecut) procs.push(isSticker ? "Individual cut" : "Die-Cut");
      if (s.transfer_tape) procs.push("Transfer Tape");
      if (s.decoration) procs.push(`Decoration${dm?.name ? ` (${dm.name})` : ""}`);
      if (s.reinforced_edges) procs.push("Reinforced Edges");
      if (s.grommets) { const gm = s.grommet_mode || "4_corners"; const cnt = gm === "every_2ft" ? Math.round(2 * ((+s.width || 0) + (+s.height || 0)) / 12 / 2) : (GROMMET_COUNTS[gm] || 0); procs.push(`Grommets (${cnt})`); }
      const sect = ss.tiled ? ` · ${ss.panels} sections of ${fmtDim(ss.panel_w)}×${fmtDim(ss.panel_h)}"` : "";
      return { dim: `${s.width}×${s.height}" ×${s.qty}`, procs: procs.join(" + ") + sect, thumb: (s.page != null ? pageThumbs[s.page] : null) };
    });
    let sectionInfo = null;
    if (isSticker && (sel.layouts || []).length) {
      const L0 = sel.layouts[0];
      const dim = L0?.sheet_height ? `${Math.round(L0.bin_width)}"×${Math.round(L0.sheet_height)}"` : "";
      const secs = sel.total?.sections_count || 0;
      sectionInfo = `${dim}${secs ? ` · ${secs} section${secs > 1 ? "s" : ""}` : ""}`;
    } else if (!isSticker) {
      const Ls = sel.layouts || (sel.layout ? [sel.layout] : []);
      if (Ls.length) {
        const w = Math.round(sel.material?.printable_width || Ls[0]?.bin_width || 0);
        const len = Math.round(Math.max(...Ls.map((L) => L.used_length || 0)));
        const n = Ls.reduce((a, L) => a + ((L.placements || []).length), 0);
        const secs = Ls.length;
        sectionInfo = `${w}×${len}" · ${secs > 1 ? Math.round(n / secs) : n} pieces · ${secs} section${secs > 1 ? "s" : ""}`;
      }
    }
    let stickers = [];
    if (isSticker) {
      const V = (nestVariants && nestVariants.length) ? nestVariants[nestIdx] : null;
      const L = V || (sel.layouts || (sel.layout ? [sel.layout] : []))[0];
      const SH = (L && L.sheet_height) || 38;
      const secByLabel = {};
      ((L && L.placements) || []).forEach((p) => {
        const sc = Math.floor((p.y + 0.001) / SH) + 1;
        secByLabel[p.label] = secByLabel[p.label] || {};
        secByLabel[p.label][sc] = (secByLabel[p.label][sc] || 0) + 1;
      });
      stickers = sizes.map((s, i) => {
        const key = `${+s.width}x${+s.height}`;
        const dist = secByLabel[key] || {};
        const nums = Object.keys(dist).map(Number).sort((a, b) => a - b);
        const distStr = nums.length === 0 ? "" : nums.length === 1 ? `Section ${nums[0]}` : `Sections ${nums.map((n) => `${n} (${dist[n]})`).join(", ")}`;
        return { label: `Sticker ${i + 1}`, dim: `${s.width}"×${s.height}"`, qty: +s.qty || 0, procs: lines[i].procs, dist: distStr, thumb: (s.page != null ? pageThumbs[s.page] : null) };
      });
    }
    return { machine: sel.total.machine_name || "—", material: sel.material.name, lamCount, lamFinish, total, lines, stickers, prodTime: prodTimeLabel(), sectionInfo };
  };
  const printTicket = () => {
    const t = buildTicket();
    if (!t) return;
    const stamp = new Date().toLocaleString();
    const esc = (v) => String(v == null ? "" : v).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const tImg = (u) => u ? `<img src="${u}" style="height:34px;width:34px;object-fit:contain;vertical-align:middle;margin-right:8px;border:1px solid #E2E8F0;border-radius:4px;background:#fff" />` : "";
    const rows = (t.stickers && t.stickers.length)
      ? t.stickers.map((st) => `<tr><td style="padding:5px 12px;font-weight:700;white-space:nowrap">${tImg(st.thumb)}${esc(st.label)} · ${esc(st.dim)} ×${st.qty}</td><td style="padding:5px 12px">${esc(st.procs)}${st.dist ? " · " + esc(st.dist) : ""}</td></tr>`).join("")
      : t.lines.map((l) => `<tr><td style="padding:5px 12px;font-weight:700;white-space:nowrap">${tImg(l.thumb)}${esc(l.dim)}</td><td style="padding:5px 12px">${esc(l.procs)}</td></tr>`).join("");
    const html = `<!doctype html><html><head><title>Production Ticket ${esc(orderNo)}</title>
      <style>body{font-family:ui-monospace,Menlo,Consolas,monospace;color:#0A0A0A;padding:28px;max-width:720px}
      h1{font-size:20px;margin:0 0 2px;letter-spacing:2px}.sub{color:#64748B;font-size:12px;margin-bottom:18px}
      .grid{display:grid;grid-template-columns:auto 1fr;gap:5px 18px;font-size:13px;margin-bottom:16px}
      .k{color:#64748B;text-transform:uppercase;font-size:11px;letter-spacing:1px}
      table{border-collapse:collapse;width:100%;font-size:13px;border-top:2px solid #0A0A0A}
      tr{border-bottom:1px solid #E2E8F0}.foot{margin-top:20px;font-size:12px;color:#64748B;border-top:1px dashed #CBD5E1;padding-top:8px}</style>
      </head><body>
      <h1>PRODUCTION TICKET</h1><div class="sub">Print and Save · production instructions (no pricing)</div>
      <div class="grid">
        <span class="k">Order #</span><span>${esc(orderNo) || "—"}</span>
        <span class="k">Order Name</span><span>${esc(orderName) || "—"}</span>
        <span class="k">Created</span><span>${esc(quoteStamp) || "—"}</span>
        <span class="k">Machine</span><span>${esc(t.machine)}</span>
        <span class="k">Material</span><span>${esc(t.material)}</span>
        ${t.sectionInfo ? `<span class="k">${isSticker ? "Section size" : "Sections"}</span><span>${esc(t.sectionInfo)}</span>` : ""}
        <span class="k">Laminate</span><span>${t.lamCount ? `${esc(t.lamFinish || "Yes")} · x${t.lamCount} pc` : "None"}</span>
        <span class="k">Total elements</span><span>${t.total} pc</span>
        <span class="k">Production time</span><span>${esc(t.prodTime)}</span>
      </div>
      <table><tbody>${rows}</tbody></table>
      <div class="foot">Printed: ${esc(stamp)}</div>
      </body></html>`;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Allow pop-ups to print the ticket"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  const calc = async () => {
    try {
      const ids = typeMats.map((m) => m.id);
      const body = { sizes: sizes.map((s) => ({ width: +s.width, height: +s.height, qty: +s.qty, diecut: !!s.diecut && finishOpts.includes("diecut"), transfer_tape: !!s.transfer_tape && finishOpts.includes("transfer_tape"), laminate: !!s.laminate && finishOpts.includes("laminate"), decoration: !!s.decoration && finishOpts.includes("decoration"), reinforced_edges: !!s.reinforced_edges && finishOpts.includes("reinforced"), grommets: !!s.grommets && finishOpts.includes("grommets"), grommet_mode: s.grommet_mode || "", laminate_material_id: s.laminate_material_id || "", decoration_material_id: s.decoration_material_id || "", misc: (s.misc || []).filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty })) })).filter((s) => +s.width > 0 && +s.height > 0), mode, laminate, transfer_tape: transferTape,
        extras: extras.map((e) => ({ type: e.type, material_id: e.material_id, width: +e.width || 0, height: +e.height || 0, qty: +e.qty || 1, die_cut: !!e.die_cut, transfer_tape: !!e.transfer_tape })),
        material_ids: ids.length ? ids : undefined,
        machine_id: machineId !== "none" ? machineId : null, sticker: isSticker, designs: +designs || 1, ink_coverage_pct: inkCoverage, spacing: +nestSpacing || 0, services,
        nest_used_length: (nestVariants && nestVariants.length)
          ? Math.max(0, ...((nestVariants[nestIdx]?.placements) || []).map((p) => (p.y || 0) + (p.h || 0))) || undefined
          : undefined,
        nest_material_id: (nestVariants && nestVariants.length) ? (selRef.current || materialId || undefined) : undefined };
      const { data } = await api.post("/calc/largeformat", body);
      setRes(data);
      const wantId = materialId || selRef.current;
      setSel(data.results.find((r) => r.material?.id === wantId) || data.results[0] || null);
      setQuoteStamp((p) => p || new Date().toLocaleString());
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  useRequote((rq) => {
    if (Array.isArray(rq.sizes) && rq.sizes.length) setSizes(rq.sizes);
    if (rq.mode) setMode(rq.mode);
    if (rq.laminate != null) setLaminate(rq.laminate);
    if (rq.transferTape != null) setTransferTape(rq.transferTape);
    if (Array.isArray(rq.extras)) setExtras(rq.extras);
    if (rq.machineId) setMachineId(rq.machineId);
    if (rq.inkCoverage != null) setInkCoverage(rq.inkCoverage);
    if (rq.typeKey) setTypeKey(rq.typeKey);
    if (rq.familyKey) setFamilyKey(rq.familyKey);
    if (rq.materialId) setMaterialId(rq.materialId);
    if (rq.nestSpacing != null) setNestSpacing(rq.nestSpacing);
    if (Array.isArray(rq.nestVariants)) setNestVariants(rq.nestVariants);
    if (rq.nestIdx != null) setNestIdx(rq.nestIdx);
    if (rq.designs != null) setDesigns(rq.designs);
    if (rq.orderNo != null) setOrderNo(rq.orderNo);
    if (rq.orderName != null) setOrderName(rq.orderName);
    if (rq.pageThumbs) setPageThumbs(rq.pageThumbs);
  }, calc, { moduleKey: cfg.moduleKey, inputs: { sizes, mode, laminate, transferTape, extras, machineId, inkCoverage, typeKey, familyKey, materialId, nestSpacing, nestVariants, nestIdx, designs, orderNo, orderName, pageThumbs }, hasResult: !!sel });

  // Keep the user's chosen material across recalcs
  useEffect(() => { selRef.current = sel?.material?.id || null; }, [sel]);
  // Live re-quote: once results exist, any input change re-runs calc (debounced) so layout + all values stay in sync
  useEffect(() => {
    if (!res) return;
    const t = setTimeout(() => { calc(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [JSON.stringify(sizes), mode, laminate, transferTape, JSON.stringify(extras), machineId, inkCoverage, typeKey]);
  // Switching the shown "Rearrange tiles" variant changes the roll consumption / fill height, so
  // re-run the quote so LF price + Sticker 12" band stay in sync with the layout actually shown.
  useEffect(() => {
    if (!res) return;
    const t = setTimeout(() => { calc(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [nestIdx, nestVariants]);
  // The Spacing gap changes the packed layout (stickers) and roll material used (Large Format),
  // so changing it re-runs the quote for both.
  useEffect(() => {
    if (!res) return;
    const t = setTimeout(() => { calc(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [nestSpacing]);
  // Re-nesting is tied to the current pieces/roll/spacing; drop stale arrangements when they change.
  // Re-nesting follows the current pieces/roll: if a rearranged layout is active, regenerate it
  // (keeping the chosen spacing) when pieces or roll change — only Reset clears it.
  useEffect(() => {
    if (nestVariants && nestVariants.length) { fetchVariants(); }
    // eslint-disable-next-line
  }, [JSON.stringify(sizes), sel?.material?.id]);

  const capOk = (m) => (mode === "diecut" || mode === "diecut_only" || mode === "cut") ? !!m.cuttable : (m.printable !== false);
  const lamMats = rollMats.filter((m) => subtypeKey(m) === "lamination");
  const decoMats = rollMats.filter((m) => ["decoration", "vinyl", "special"].includes(subtypeKey(m)));
  const defLam = lamMats.find((m) => m.is_default) || lamMats[0];
  const defDeco = decoMats.find((m) => subtypeKey(m) === "decoration" && m.is_default) || decoMats.find((m) => m.is_default) || decoMats[0];
  const pickMats = rollMats.filter(capOk);
  const availTypes = [...new Set(pickMats.map((m) => subtypeKey(m)))]
    .sort((a, b) => (LF_SUBTYPES[a]?.order || 99) - (LF_SUBTYPES[b]?.order || 99));
  const typeMats = pickMats.filter((m) => subtypeKey(m) === typeKey);
  const finishOpts = isSticker ? ["diecut", "transfer_tape", "laminate"] : (FINISH_BY_SUBTYPE[typeKey] || ["diecut", "transfer_tape", "laminate", "decoration"]);
  const familyList = [...new Set(typeMats.map((m) => familyName(m)))];
  const famRep = (f) => [...typeMats.filter((m) => familyName(m) === f)].sort((a, b) => (a.unit_cost || 0) - (b.unit_cost || 0))[0];
  const famMats = [...typeMats.filter((m) => familyName(m) === familyKey)].sort((a, b) => (b.printable_width || 0) - (a.printable_width || 0));
  const famLabel = (f) => (f === familyKey && selMat) ? (selMat.name || famRep(f)?.name || f) : (famRep(f)?.name || f);
  const selMat = famMats.find((m) => m.id === materialId) || famMats[0];
  // Preselect the default (cheapest/basic) roll for the current type — VersaWorks "Get Media" starts loaded.
  useEffect(() => {
    if (!typeMats.length) return;
    if (materialId && typeMats.some((m) => m.id === materialId)) { setFamilyKey(familyName(typeMats.find((m) => m.id === materialId))); return; }
    const def = typeMats.find((m) => m.is_default) || [...typeMats].sort((a, b) => (a.unit_cost || 0) - (b.unit_cost || 0))[0];
    if (def) { setFamilyKey(familyName(def)); setMaterialId(def.id); }
    // eslint-disable-next-line
  }, [typeKey, rollMats]);
  // Default / re-sync the selected material TYPE when the material list or mode changes.
  // Large Format defaults to Vinyl when available; Stickers keep the natural order.
  useEffect(() => {
    if (availTypes.length && (!typeKey || !availTypes.includes(typeKey))) {
      const preferred = (!isSticker && availTypes.includes("vinyl")) ? "vinyl" : availTypes[0];
      setTypeKey(preferred);
    }
    // eslint-disable-next-line
  }, [rollMats, mode]);
  // When a Compare card is clicked, show that material in the main panel
  useEffect(() => {
    if (res && materialId) { const f = res.results.find((r) => r.material?.id === materialId); if (f) setSel(f); }
    // eslint-disable-next-line
  }, [materialId]);

  const totalPieces = sizes.reduce((a, s) => a + (+s.qty || 0), 0);
  const thumbsByKey = buildThumbsByKey(pageThumbs, sizes);
  // Per-row production file: open the Cut & Bleed engine prefilled with the row's size + uploaded artwork.
  const makeCutFile = (row) => {
    if (!row.art_id) { toast.error("This line has no uploaded artwork"); return; }
    if (!(+row.width > 0 && +row.height > 0)) { toast.error("Set a size first"); return; }
    setCutRow(row);
  };
  // On save, rasterize the sheet layout with the real logos → store a light JPEG mockup in object storage.
  const buildAndUploadMockup = async () => {
    if (!Object.keys(pageThumbs || {}).length) return {};
    try {
      const L = (sel?.layouts || (sel?.layout ? [sel.layout] : []))[0];
      if (!L) return {};
      const blob = await renderMockupBlob(L, thumbsByKey);
      if (!blob) return {};
      const up = await uploadArtBlob(blob, `sheet-mockup-${Date.now()}.jpg`);
      return up?.file_id ? { mockup_id: up.file_id } : {};
    } catch (e) { return {}; }
  };

  return (
    <div data-testid={cfg.pageTestId}>
      <PageHeader title={cfg.title} eyebrow="Live Pricing" subtitle={cfg.subtitle} />
      <div className="p-8">
        <Tabs defaultValue="estimate">
          <TabsList className="rounded-full bg-slate-100 p-1">
            <TabsTrigger value="estimate" data-testid="tab-estimate" className="rounded-full">Estimating</TabsTrigger>
            {isAdmin && <TabsTrigger value="materials" data-testid="tab-materials" className="rounded-full">Roll Materials</TabsTrigger>}
            {isAdmin && <TabsTrigger value="presets" data-testid="tab-presets" className="rounded-full">Size Presets</TabsTrigger>}
            {isAdmin && <TabsTrigger value="intel" data-testid="tab-intel" className="rounded-full">Price Intel</TabsTrigger>}
            <TabsTrigger value="misc" data-testid="tab-misc" className="rounded-full">Miscellaneous</TabsTrigger>
          </TabsList>

          <TabsContent value="estimate" className="mt-6 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl p-6 h-fit">
              {isAdmin && (
                <div className="mb-5" data-testid="lf-type-picker">
                  <label className="text-xs font-semibold text-slate-600">Material type</label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {availTypes.map((sk) => {
                      const g = LF_SUBTYPES[sk] || {};
                      const active = typeKey === sk;
                      return (
                        <button key={sk} type="button" data-testid={`lf-type-btn-${sk}`} onClick={() => setTypeKey(sk)}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: g.color }} />
                          {g.label || sk}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1.5">Enter your sizes — the system picks the best roll width(s) and compares every {(LF_SUBTYPES[typeKey]?.label || "material")} roll.</p>
                </div>
              )}
              {typeMats.length > 0 && (
                <div className="mb-5 grid grid-cols-3 gap-3 items-start" data-testid="lf-media-picker">
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-slate-600 flex items-center h-5">Material</label>
                    <select data-testid="lf-material-select" className="mt-1.5 w-full h-9 rounded-lg border border-slate-300 text-sm px-2 bg-white"
                      value={familyKey} onChange={(e) => { const f = e.target.value; setFamilyKey(f); const d = famRep(f); if (d) setMaterialId(d.id); }}>
                      {familyList.map((f) => <option key={f} value={f}>{famLabel(f)}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <label className="text-xs font-semibold text-slate-600 flex items-center gap-1 h-5"><Layers size={12} />Get Media<HelpHint side="top"><b>Get Media</b> is the roll width used for this job. The system compares every roll of the selected material and auto-loads the best fit &mdash; change it to force a specific roll width.</HelpHint></label>
                    <select data-testid="lf-getmedia-select" className="mt-1.5 w-full h-9 rounded-lg border border-slate-300 text-sm px-2 bg-white"
                      value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
                      {famMats.map((m) => <option key={m.id} value={m.id}>{num(m.roll_width, 0)}"</option>)}
                    </select>
                    {selMat && <p className="text-[10px] font-mono text-slate-400 mt-1" data-testid="lf-getmedia-printable">{num(selMat.printable_width, 0)}" printable</p>}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-head font-bold shrink-0">Sizes ({sizes.length}/25)</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <LFPdfImport onApply={(rows, replace, pages, masterId) => {
                    const prev = replace ? {} : { ...pageThumbs };
                    const keys = Object.keys(prev).map(Number).filter((n) => !isNaN(n));
                    const offset = (!replace && keys.length) ? Math.max(...keys) + 1 : 0;
                    const nextThumbs = { ...prev };
                    (pages || []).forEach((pg) => { if (pg && pg.thumb != null && typeof pg.index === "number") nextThumbs[pg.index + offset] = pg.thumb; });
                    const mapped = rows.map((r) => (typeof r.page === "number" ? { ...r, page: r.page + offset } : r));
                    setPageThumbs(nextThumbs);
                    setSizes(replace ? mapped.slice(0, 25) : [...sizes, ...mapped].slice(0, 25));
                  }} />
                  <HelpHint side="bottom"><b>Import PDF</b> turns a multi-page PDF into size rows &mdash; each page becomes one row with its trim size auto-filled. Ideal for quoting a whole artwork set at once.</HelpHint>
                  <LFImageImport onApply={applyImages} />
                  <HelpHint side="bottom"><b>Import Images</b> adds one or many images at once &mdash; each becomes a row with its aspect ratio locked, so you set only the width and the height follows (no distortion).</HelpHint>
                  <Select onValueChange={addPreset}>
                    <SelectTrigger data-testid="preset-select" className="rounded-lg h-9 w-36 text-xs"><SelectValue placeholder="+ Add preset" /></SelectTrigger>
                    <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button data-testid="add-size-button" onClick={addRow} size="sm" variant="outline" className="rounded-lg"><Plus size={15} /></Button>
                  {sizes.some((s) => s.art_id && +s.width > 0 && +s.height > 0) && (
                    <Button data-testid="lf-batch-cut" onClick={() => setBatchOpen(true)} size="sm" variant="outline" className="rounded-lg border-[#E6007E]/40 text-[#E6007E] hover:bg-[#E6007E]/10">
                      <Scissors size={14} className="mr-1" />Batch Cut-Line ({sizes.filter((s) => s.art_id && +s.width > 0 && +s.height > 0).length})
                    </Button>
                  )}
                  {sizes.some((s) => s.art_id && +s.width > 0 && +s.height > 0) && (
                    <HelpHint side="bottom"><b>Batch Cut-Line</b> generates a production file (bleed + CutContour line) for <b>every row that has uploaded artwork</b> at once, using shared settings, and downloads them together in one ZIP.</HelpHint>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="lf-batch-dim">
                <span className="text-[11px] font-semibold text-slate-500">Set all</span>
                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                  <button type="button" data-testid="lf-batch-dim-h" onClick={() => setBatchDim("height")} className={`px-2 py-0.5 text-[11px] transition-colors ${batchDim === "height" ? "bg-[#2495D3] text-white" : "text-slate-500 hover:bg-slate-100"}`}>Height</button>
                  <button type="button" data-testid="lf-batch-dim-w" onClick={() => setBatchDim("width")} className={`px-2 py-0.5 text-[11px] transition-colors ${batchDim === "width" ? "bg-[#2495D3] text-white" : "text-slate-500 hover:bg-slate-100"}`}>Width</button>
                </div>
                <Input data-testid="lf-batch-val" type="number" placeholder="in" value={batchVal} onChange={(e) => setBatchVal(e.target.value)} className="h-7 w-20 rounded num text-xs" />
                <Button type="button" size="sm" variant="outline" data-testid="lf-batch-apply" onClick={applyBatchDim} className="h-7 text-[11px] rounded-lg">Apply to all</Button>
                <span className="text-[10px] text-slate-400">fills the other side by each row's ratio</span>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-mono uppercase text-slate-500 px-1">
                  <span className="col-span-4">Width in</span><span className="col-span-1"></span><span className="col-span-4">Height in</span><span className="col-span-2">Qty</span><span className="col-span-1"></span>
                </div>
                {sizes.map((s, i) => (
                  <div key={i} data-testid={`size-row-${i}`} className="space-y-1">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <Input data-testid={`lf-size-w-${i}`} className="col-span-4 rounded-lg num" type="number" value={s.width} onChange={(e) => updDim(i, "width", e.target.value)} />
                      <button type="button" data-testid={`lf-chain-${i}`} onClick={() => toggleLock(i)} title="Link Width & Height to keep the proportion &mdash; edit one and the other follows. Click to unlink."
                        className={`col-span-1 flex items-center justify-center transition-colors ${s.ratioLocked ? "text-[#2495D3]" : "text-slate-300 hover:text-slate-500"}`}>
                        {s.ratioLocked ? <Link2 size={15} /> : <Link2Off size={15} />}
                      </button>
                      <Input data-testid={`lf-size-h-${i}`} className="col-span-4 rounded-lg num" type="number" value={s.height} onChange={(e) => updDim(i, "height", e.target.value)} />
                      <Input data-testid={`lf-size-qty-${i}`} className="col-span-2 rounded-lg num" type="number" value={s.qty} onChange={(e) => upd(i, "qty", e.target.value)} />
                      <button className="col-span-1 text-slate-400 hover:text-red-500" onClick={() => rmRow(i)}><X size={16} /></button>
                    </div>
                    <div className="flex items-center gap-2 pl-1 mt-0.5" data-testid={`size-aspect-${i}`}>
                      {s.art_id && +s.width > 0 && +s.height > 0 && (
                        <button type="button" onClick={() => makeCutFile(s)} data-testid={`lf-cutfile-${i}`} title="Generate print-ready PDF with 0.125&quot; bleed + CutContour kiss-cut"
                          className="inline-flex items-center gap-1 rounded-md border border-[#E6007E]/40 text-[#E6007E] px-1.5 py-0.5 text-[11px] font-medium hover:bg-[#E6007E]/10">
                          <Scissors size={11} /> Cut-Line
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pl-1 text-[11px] text-slate-500" data-testid={`size-finish-${i}`}>
                      {finishOpts.includes("diecut") && <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-diecut-${i}`} checked={!!s.diecut} onChange={(e) => upd(i, "diecut", e.target.checked)} />{isSticker ? "Individual Cut" : "Die-Cut"}</label><HelpHint side="top">{isSticker ? <span><b>Individual Cut</b> separates each sticker to its own shape. Off = pieces stay together on the sheet (kiss-cut only).</span> : <span><b>Die-Cut</b> cuts each piece to its final shape with a contour blade &mdash; an added cutting service. Off = simple straight trim.</span>}</HelpHint></span>}
                      {finishOpts.includes("transfer_tape") && <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-tape-${i}`} checked={!!s.transfer_tape} onChange={(e) => upd(i, "transfer_tape", e.target.checked)} />Transfer Tape</label><HelpHint side="top"><b>Transfer (application) tape</b> is a top layer that lets you peel many small cut pieces and apply them together, keeping their spacing. Common for cut lettering and multi-piece decals.</HelpHint></span>}
                      {finishOpts.includes("laminate") && <span className="inline-flex items-center gap-1"><label className={`flex items-center gap-1 ${selMat?.can_laminate === false ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`} title={selMat?.can_laminate === false ? "This substrate doesn't accept lamination" : ""}><input type="checkbox" disabled={selMat?.can_laminate === false} data-testid={`lf-row-laminate-${i}`} checked={!!s.laminate} onChange={(e) => upd(i, "laminate", e.target.checked)} />Lamination</label><HelpHint side="top"><b>Lamination</b> adds a clear protective film over the print for scratch, UV and water resistance (gloss or matte). Some substrates can&apos;t be laminated.</HelpHint></span>}
                      {finishOpts.includes("decoration") && <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-decoration-${i}`} checked={!!s.decoration} onChange={(e) => upd(i, "decoration", e.target.checked)} />Decoration</label><HelpHint side="top"><b>Decoration</b> applies an extra finishing material/effect to the piece (chosen below). Adds its own material cost per piece.</HelpHint></span>}
                      {finishOpts.includes("reinforced") && <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-reinforced-${i}`} checked={!!s.reinforced_edges} onChange={(e) => upd(i, "reinforced_edges", e.target.checked)} />Reinforced Edges</label><HelpHint side="top"><b>Reinforced edges</b> add a welded/taped hem around banners so grommets don&apos;t tear and the edge lasts outdoors.</HelpHint></span>}
                      {finishOpts.includes("grommets") && <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-grommets-${i}`} checked={!!s.grommets} onChange={(e) => { const on = e.target.checked; setSizes(sizes.map((row, idx) => idx === i ? { ...row, grommets: on, grommet_mode: row.grommet_mode || (on ? "4_corners" : "") } : row)); }} />Grommets</label><HelpHint side="top"><b>Grommets</b> are metal eyelets punched into the edges for hanging (e.g. 4 corners or spaced along the edge). Pick the placement after enabling.</HelpHint></span>}
                      <span className="inline-flex items-center gap-1"><label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" data-testid={`lf-row-misc-${i}`} checked={(s.misc || []).length > 0} onChange={(e) => upd(i, "misc", e.target.checked ? [{ material_id: "", qty: 1 }] : [])} />Miscellaneous</label><HelpHint side="top">Add extra chargeable items to <b>this size row</b> (per-piece), e.g. special hardware or media. Pick the item and quantity.</HelpHint></span>
                    </div>
                    {(s.misc || []).length > 0 && (
                      <div className="pl-1 mt-1" data-testid={`size-misc-${i}`}>
                        <MiscRows opts={miscOpts} value={s.misc || []} onChange={(v) => upd(i, "misc", v)} />
                      </div>
                    )}
                    {(s.laminate || s.decoration || s.grommets) && (
                      <div className="flex flex-col gap-1 pl-1" data-testid={`size-finish-mats-${i}`}>
                        {s.laminate && finishOpts.includes("laminate") && (
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-slate-400 w-20 shrink-0" style={{ color: LF_SUBTYPES.lamination.color }}>Laminate</span>
                            <Select value={s.laminate_material_id || defLam?.id || ""} onValueChange={(v) => upd(i, "laminate_material_id", v)}>
                              <SelectTrigger data-testid={`lf-row-lam-mat-${i}`} className="h-7 text-xs rounded-lg flex-1"><SelectValue placeholder="Select laminate" /></SelectTrigger>
                              <SelectContent>{lamMats.length ? lamMats.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>) : <div className="px-2 py-1.5 text-xs text-slate-400">No lamination rolls</div>}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {s.decoration && finishOpts.includes("decoration") && (
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-slate-400 w-20 shrink-0" style={{ color: LF_SUBTYPES.decoration.color }}>Decoration</span>
                            <Select value={s.decoration_material_id || defDeco?.id || ""} onValueChange={(v) => upd(i, "decoration_material_id", v)}>
                              <SelectTrigger data-testid={`lf-row-deco-mat-${i}`} className="h-7 text-xs rounded-lg flex-1"><SelectValue placeholder="Select vinyl" /></SelectTrigger>
                              <SelectContent>{decoMats.length ? decoMats.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>) : <div className="px-2 py-1.5 text-xs text-slate-400">No vinyl rolls</div>}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {s.grommets && finishOpts.includes("grommets") && (
                          <div className="flex items-center gap-2 text-[11px]">
                            <span className="text-slate-400 w-20 shrink-0" style={{ color: LF_SUBTYPES.banner?.color || "#EA580C" }}>Grommets</span>
                            <Select value={s.grommet_mode || "4_corners"} onValueChange={(v) => upd(i, "grommet_mode", v)}>
                              <SelectTrigger data-testid={`lf-row-grommet-mode-${i}`} className="h-7 text-xs rounded-lg flex-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const gr = Number(lfSet.lf_grommet_price_each ?? 1);
                                  const perimFt = 2 * ((+s.width || 0) + (+s.height || 0)) / 12;
                                  const opts = [
                                    { k: "top_center", l: "Top Center", c: 1 },
                                    { k: "top_corners", l: "Top Corners", c: 2 },
                                    { k: "4_corners", l: "4 Corners", c: 4 },
                                    { k: "6_grommets", l: "4 Corners + 2 Middle", c: 6 },
                                    { k: "every_2ft", l: "Every 2 ft", c: Math.round(perimFt / 2) },
                                  ];
                                  return opts.map((o) => <SelectItem key={o.k} value={o.k}>{o.k === "every_2ft" ? o.l : `${o.l} (${o.c} pc) · ${money(o.c * gr)}`}</SelectItem>);
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    )}
                    {(() => {
                      const w = +s.width, h = +s.height;
                      const pw = selMat?.printable_width || sel?.material?.printable_width || 52;
                      if (!(w > 0 && h > 0) || Math.min(w, h) <= pw) return null;
                      const pn = Math.max(1, Math.ceil(w / pw));
                      return (
                        <div data-testid={`size-oversize-${i}`} className="flex items-center gap-1.5 pl-1 text-[11px] text-amber-600">
                          <AlertTriangle size={12} className="shrink-0" />
                          Oversize — prints in {pn} sections of {fmtDim(w / pn)}×{fmtDim(h)}"
                        </div>
                      );
                    })()}
                    {(s.name || s.page != null) && (
                      <div className="flex items-center gap-2 pl-1" data-testid={`size-art-${i}`}>
                        {s.page != null && pageThumbs[s.page] && <img src={pageThumbs[s.page]} alt="" onClick={() => setZoom(pageThumbs[s.page])} title="Click to zoom" className="h-9 w-9 object-contain rounded border border-slate-200 bg-white shrink-0 cursor-zoom-in hover:ring-2 hover:ring-[#2495D3]" data-testid={`size-thumb-${i}`} />}
                        <span className="text-[11px] text-slate-500 truncate flex-1" title={s.name}>{s.name || "Imported artwork"}</span>
                        <span className="text-[10px] font-mono uppercase text-slate-400 shrink-0">Art pg</span>
                        <Input data-testid={`size-page-${i}`} className="h-7 w-16 rounded num text-xs shrink-0" type="number" min={1} placeholder="—"
                          value={s.page != null ? s.page + 1 : ""}
                          onChange={(e) => upd(i, "page", e.target.value === "" ? null : Math.max(0, (parseInt(e.target.value) || 1) - 1))} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {isAdmin && <InkPicker machineId={machineId} setMachineId={setMachineId} coverage={inkCoverage} setCoverage={setInkCoverage} categories={["largeformat"]} estimateSize={sizes[0]} defaultMatch="mimaki" />}
              {isSticker && (
                <div className="mt-4" data-testid="sticker-designs-block">
                  <Label className="text-xs flex items-center gap-1">Different designs (same size)<HelpHint side="right">How many distinct artworks share this same size. The total quantity is split across them; each extra design adds a small setup surcharge.</HelpHint></Label>
                  <Input data-testid="sticker-designs" type="number" min={1} max={4} value={designs} onChange={(e) => setDesigns(e.target.value)} className="h-9 rounded-lg mt-1 num" />
                  <p className="text-[11px] text-slate-400 mt-1">Splits the quantity across designs (adds a surcharge per extra design).</p>
                </div>
              )}
              <ServicesPicker module={isSticker ? "stickers" : "large-format"} onChange={setServices} />
              <Button data-testid="calc-lf-button" onClick={() => calc()} className="w-full mt-5 bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg h-11">
                <Calculator size={16} className="mr-2" />Generate Quote
              </Button>
              {sel && (
                <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="lf-production-ticket">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500"><ClipboardList size={14} />Production Ticket</div>
                    <Button type="button" size="sm" variant="outline" data-testid="lf-print-ticket" onClick={printTicket} className="h-7 text-xs rounded-lg"><Printer size={13} className="mr-1" />Print</Button>
                  </div>
                  {quoteStamp && <div className="text-[10px] text-slate-400 -mt-2 mb-3" data-testid="lf-ticket-created">Created: {quoteStamp}</div>}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div><Label className="text-[10px] font-mono uppercase text-slate-400">Order #</Label><Input data-testid="lf-order-no" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} className="h-8 rounded-lg text-sm mt-0.5" placeholder="e.g. LF-1024" /></div>
                    <div><Label className="text-[10px] font-mono uppercase text-slate-400">Order Name</Label><Input data-testid="lf-order-name" value={orderName} onChange={(e) => setOrderName(e.target.value)} className="h-8 rounded-lg text-sm mt-0.5" placeholder="Customer / job" /></div>
                  </div>
                  {(() => { const t = buildTicket(); if (!t) return null; return (
                    <div className="text-xs space-y-1.5" data-testid="lf-ticket-body">
                      <div className="flex justify-between gap-3"><span className="text-slate-400">Machine</span><span className="font-semibold text-right">{t.machine}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-slate-400">Material</span><span className="font-semibold text-right">{t.material}</span></div>
                      {t.sectionInfo && <div className="flex justify-between gap-3" data-testid="lf-ticket-sections"><span className="text-slate-400">{isSticker ? "Section size" : "Sections"}</span><span className="font-semibold text-[#2495D3]">{t.sectionInfo}</span></div>}
                      <div className="flex justify-between gap-3"><span className="text-slate-400">Laminate</span><span className="font-semibold text-right">{t.lamCount ? `${t.lamFinish || "Yes"} · ×${t.lamCount} pc` : "None"}</span></div>
                      <div className="flex justify-between gap-3"><span className="text-slate-400">Total elements</span><span className="font-semibold">{t.total} pc</span></div>
                      {sel.total.banner_addon_cost > 0 && <div className="flex justify-between gap-3" data-testid="lf-ticket-banner-addon"><span className="text-slate-400">Banner finishing{sel.total.grommet_count ? ` · ${sel.total.grommet_count} grommets` : ""}</span><span className="font-semibold text-right">{money(sel.total.banner_addon_cost)}</span></div>}
                      {t.stickers && t.stickers.length > 0 ? (
                        <div className="border-t border-slate-100 pt-2 mt-1 space-y-1.5" data-testid="lf-ticket-stickers">
                          {t.stickers.map((st, i) => (
                            <div key={i} data-testid={`lf-ticket-sticker-${i}`}>
                              <div className="flex justify-between gap-3">
                                <span className="font-semibold text-slate-700 num">{st.label} · {st.dim}</span>
                                <span className="font-semibold num">×{st.qty}</span>
                              </div>
                              <div className="text-slate-500 pl-1">{st.procs}{st.dist ? ` · ${st.dist}` : ""}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="border-t border-slate-100 pt-2 mt-1 space-y-1">
                          {t.lines.map((l, i) => (
                            <div key={i} className="flex gap-2" data-testid={`lf-ticket-line-${i}`}>
                              <span className="font-semibold text-slate-700 whitespace-nowrap num">{l.dim}</span>
                              <span className="text-slate-500">— {l.procs}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between border-t border-slate-100 pt-2 mt-1"><span className="text-slate-400">Production time</span><span className="font-semibold text-[#2495D3]">{t.prodTime}</span></div>
                    </div>
                  ); })()}
                </div>
              )}
            </div>

            <div className="lg:col-span-7">
              {!res || !sel ? (
                <EmptyState>Add sizes and compare compatible materials.</EmptyState>
              ) : (
                <div className="space-y-6" data-testid="lf-results">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Metric icon={Layers} label={isSticker ? "Ordered" : "Pieces"} value={totalPieces} sub={isSticker && sel?.total?.sections_count ? `${sel.total.sections_count} section${sel.total.sections_count > 1 ? "s" : ""}` : undefined} />
                    <Metric icon={Ruler} label="Print Area" value={`${num(sel.material.printable_width, 0)}"×${Math.round(jobLinearFt() * 12)}"`} sub={<>
                      <span className="block">{jobLinearFt().toFixed(1)} ft</span>
                      {sel.material.retail_price != null && <span className="block" data-testid="lf-print-retail-sqft">Retail {money(sel.material.retail_price)}/ft²</span>}
                      {sel.material.wholesale_price != null && <span className="block" data-testid="lf-print-ws-sqft">WS {money(sel.material.wholesale_price)}/ft²</span>}
                    </>} />
                    {sell(sel.total) != null && (() => {
                      const rF = rushInfo.factor;
                      const retailIncl = sel.total.selling_price != null ? sel.total.selling_price * rF * retailTaxF : null;
                      const wsIncl = sel.total.wholesale_price != null ? sel.total.wholesale_price * rF * wsTaxF : null;
                      const big = retailIncl != null ? retailIncl : (wsIncl != null ? wsIncl : sell(sel.total) * rF);
                      const label = (sel.total.selling_price != null ? "Retail" : "Wholesale") + " (incl. tax)";
                      const sub = (retailIncl != null && wsIncl != null) ? `WS ${money(wsIncl)} incl. tax` : undefined;
                      return <Metric icon={Tag} label={label} value={money(big)} sub={sub} accent />;
                    })()}
                    <Metric icon={DollarSign} label="Material" value={money(sel.total.material_cost ?? 0)} sub={<><span className="block" data-testid="lf-material-print">Print {money(sel.total.printing_cost ?? 0)}</span><span className="block" data-testid="lf-material-production">Production {money(sel.total.base_cost ?? 0)}</span></>} />
                  </div>

                  {pieceRows().length > 0 && (
                    <div className="border border-slate-200 rounded-xl overflow-x-auto" data-testid="lf-per-piece">
                      <div className="px-3 pt-3">
                        <SectionLabel>{sel.material.name} · Layout</SectionLabel>
                        {sel.material.strips_summary && (
                          <span className="inline-block text-[10px] font-mono uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full mt-1" data-testid="lf-chosen-width">Suggested roll(s): {sel.material.strips_summary}</span>
                        )}
                        {(() => {
                          const job = jobLinearFt(); const avail = availLinearFt(); const rem = avail - job; const ok = rem >= 0;
                          return (
                            <div data-testid="lf-media-consumption" className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 mt-2 text-xs border ${avail <= 0 ? "bg-slate-50 border-slate-200 text-slate-500" : ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                              <span className="num flex items-center gap-1.5">
                                {avail > 0 && (ok ? <Check size={14} /> : <AlertTriangle size={14} />)}
                                Uses <b>{job.toFixed(1)} ft</b> of {sel.material.name}
                              </span>
                              <span className="num">{avail > 0 ? (ok ? `${rem.toFixed(1)} ft left in stock` : `Short by ${Math.abs(rem).toFixed(1)} ft`) : "stock n/a"}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="text-[10px] font-mono uppercase text-slate-400 px-3 pt-2 pb-1">Per-piece pricing (net profit = retail excl. tax − real cost)</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[9px] font-mono uppercase text-slate-400 border-b border-slate-100">
                            <th className="text-left px-3 py-1.5">Piece</th>
                            <th className="text-right px-2">Material</th>
                            <th className="text-right px-2">Retail</th>
                            <th className="text-right px-2">+tax</th>
                            <th className="text-right px-2">WS</th>
                            <th className="text-right px-2">+tax</th>
                            <th className="text-right px-3">Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pieceRows().map((p) => {
                            const secs = (p.panels || 1) * (p.qty || 1);
                            const per = p.tiled && secs > 1 ? { material: p.material / secs, retail: p.retail / secs, ws: p.ws / secs, profit: p.profit / secs } : null;
                            return (
                            <Fragment key={p.i}>
                            <tr data-testid={`lf-piece-${p.i}`} className="border-b border-slate-50 last:border-0 num">
                              <td className="text-left px-3 py-2 text-slate-600 whitespace-nowrap">{p.w}×{p.h}" <span className="text-slate-400">×{p.qty}</span>
                                {p.qty > 1 && <span data-testid={`lf-piece-unit-${p.i}`} className="block text-[10px] text-slate-400 mt-0.5"><span className="text-[#2495D3] font-semibold">{money(p.retail / p.qty)}/pc retail</span> · {money(p.ws / p.qty)}/pc WS</span>}
                              </td>
                              <td className="text-right px-2 text-slate-500">{money(p.material)}</td>
                              <td className="text-right px-2 font-semibold text-[#2495D3]">{money(p.retail)}</td>
                              <td className="text-right px-2 text-slate-400">{money(p.retail * retailTaxF)}</td>
                              <td className="text-right px-2 text-slate-600">{money(p.ws)}</td>
                              <td className="text-right px-2 text-slate-400">{money(p.ws * wsTaxF)}</td>
                              <td className={`text-right px-3 font-bold ${p.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{money(p.profit)}</td>
                            </tr>
                            {per && (
                            <tr data-testid={`lf-piece-section-${p.i}`} className="border-b border-slate-50 last:border-0 num bg-amber-50/50 text-[11px]">
                              <td className="text-left px-3 py-1.5 pl-7 text-amber-700 whitespace-nowrap">↳ per section · {fmtDim(p.panel_w)}×{fmtDim(p.panel_h)}" <span className="text-amber-400">× {secs}</span></td>
                              <td className="text-right px-2 text-slate-500">{money(per.material)}</td>
                              <td className="text-right px-2 font-semibold text-[#2495D3]">{money(per.retail)}</td>
                              <td className="text-right px-2 text-slate-400">{money(per.retail * retailTaxF)}</td>
                              <td className="text-right px-2 text-slate-600">{money(per.ws)}</td>
                              <td className="text-right px-2 text-slate-400">{money(per.ws * wsTaxF)}</td>
                              <td className={`text-right px-3 font-bold ${per.profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{money(per.profit)}</td>
                            </tr>
                            )}
                            </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {sel.total.min_charge_applied && (
                    <div data-testid="lf-min-badge" className="flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">
                      <Info size={14} className="shrink-0" />
                      <span>Minimum charge applied. This job uses less than 1 linear foot of roll, so it's billed at the minimum (1 ft × roll width).</span>
                    </div>
                  )}

                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <SectionLabel>Layout &amp; nesting</SectionLabel>
                    {sel.total.machine_name && (
                      <div className="flex justify-between text-xs num text-[#2495D3] pb-2 mb-1 border-b border-slate-100" data-testid="lf-ink-line">
                        <span>Ink · {sel.total.machine_name} · {sel.total.ink_ml} ml</span>
                        <span>{sel.total.ink_included_in_override ? <span className="text-slate-400">incl. in price</span> : money(sel.total.ink_cost)}</span>
                      </div>
                    )}
                    {sel.total.extras_cost > 0 && (
                      <div className="flex justify-between text-xs num text-pink-600 pb-2 mb-1 border-b border-slate-100" data-testid="lf-extras-line">
                        <span>Extras (lamination / decoration)</span>
                        <span>{money(sel.total.extras_cost)}</span>
                      </div>
                    )}
                    {isAdmin && sel.total.sections?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Button data-testid="lf-prod-sheet" size="sm" variant="outline" className="rounded-lg text-xs" onClick={dlProd}>Production Sheet PDF</Button>
                        <HelpHint side="top"><b>Production Sheet PDF</b> is a shop-floor layout map: it shows how every piece is nested on the roll (positions, sizes, sections) so your operator can print and cut exactly as quoted. It does not contain the artwork.</HelpHint>
                        <Button data-testid="lf-impose-btn" size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => imposeRef.current?.click()}>Nested Print PDF (upload master)</Button>
                        <HelpHint side="top"><b>Nested Print PDF</b>: upload one master artwork and the system imposes (nests) it into the full ready-to-print sheet at the exact tile positions from the layout &mdash; the file you send to the printer/RIP.</HelpHint>
                        <input ref={imposeRef} type="file" accept="application/pdf" onChange={dlImpose} className="hidden" data-testid="lf-impose-file" />
                      </div>
                    )}
                    {isSticker && (() => {
                      const f = stickerFill();
                      if (!f) return null;
                      return (
                        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs mb-3" data-testid="sticker-section-fill">
                          <div className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                            <Layers size={13} className="text-[#2495D3]" /> Section fill
                            <span className="font-bold text-[#2495D3] ml-1">{f.secDim}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {f.rows.map((r) => (
                              <div key={r.i} className={`num rounded-md border px-2 py-1 ${r.full ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid={`sticker-fill-row-${r.i}`}>
                                <span className="text-slate-500">Sec {r.i}: </span>
                                <span className="text-slate-600">{r.dim}{r.mixed ? " +" : ""} · </span>
                                <span className={r.full ? "text-emerald-700 font-semibold" : "text-slate-700 font-semibold"}>{r.count} pc</span>
                                {r.full
                                  ? <span className="ml-1 text-emerald-600">· full</span>
                                  : <span className="ml-1 text-amber-600">· add {r.add} more</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {sel?.total?.min_charge_hit && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 mb-3 flex items-center gap-2" data-testid="lf-min-charge-warning">
                        <AlertTriangle size={14} className="text-amber-500 shrink-0" />
                        <span>Minimum charge applied (${(sel.total.min_charge ?? 20).toFixed(2)}). Add more pieces to fill the section for a better per-unit price.</span>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="lf-rearrange-bar">
                      <Button type="button" size="sm" variant="outline" data-testid="lf-rearrange-btn" onClick={rearrange} className="h-8 rounded-lg text-xs border-[#2495D3] text-[#2495D3] hover:bg-blue-50">
                        <RotateCw size={13} className="mr-1.5" />Rearrange tiles{nestVariants ? ` (${nestIdx + 1}/${nestVariants.length})` : ""}
                      </Button>
                      <HelpHint side="top"><b>Rearrange tiles</b> tries alternative nesting layouts (rotating and repacking pieces) to use less roll. Click again to cycle options; the price and fill update to match the shown layout. <b>Reset</b> returns to the automatic best fit.</HelpHint>
                      {nestVariants && (
                        <Button type="button" size="sm" variant="ghost" data-testid="lf-rearrange-reset" onClick={() => { setNestVariants(null); setNestIdx(0); setNestSpacing(0); }} className="h-8 rounded-lg text-xs text-slate-500">Reset</Button>
                      )}
                      <label className="flex items-center gap-1 text-[11px] text-slate-500 num">Spacing<HelpHint side="top">Gap (in inches) left between pieces for the kiss-cut/knife. Bigger spacing is safer for cutting but fits fewer pieces per sheet and uses a little more material.</HelpHint>
                        <Input type="number" step="0.125" min="0" data-testid="lf-nest-spacing" value={nestSpacing} onChange={(e) => setNestSpacing(e.target.value)} className="h-8 w-16 rounded-lg text-xs num" />"
                      </label>
                    </div>
                    {nestVariants && nestVariants.length ? (() => {
                      const V = nestVariants[nestIdx];
                      return (
                        <div className="mb-2" data-testid="lf-nest-variant">
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-1" data-testid="lf-nest-variant-info">
                            <span>{V.label} · {V.util_pct}% used</span>
                            <span>{V.linear_feet} ft ({V.used_length}") · {V.area_sqft} ft²</span>
                          </div>
                          <NestingCanvas layout={V} thumbs={thumbsByKey} sectionInfo={lfSecLine(V, 1)} fit={nestFit} onFitChange={setNestFit} />
                        </div>
                      );
                    })() : (sel.layouts || (sel.layout ? [sel.layout] : [])).map((L, i, arr) => (
                      <div key={i} className="mb-2" data-testid={`lf-section-${i}`}>
                        {arr.length > 1 && (
                          <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-500 mb-0.5">
                            <span>Section {i + 1} of {arr.length} · {Math.round(sel.material?.printable_width || L.bin_width || 0)}×{Math.round(L.used_length || 0)}"</span>
                            {L.oversize && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold" data-testid={`lf-section-scaled-${i}`}><AlertTriangle size={11} />SCALED FOR PRINT</span>}
                          </div>
                        )}
                        {L.util_pct != null && (
                          <div className="text-[10px] font-mono uppercase text-slate-400 mb-1" data-testid={`lf-util-${i}`}>
                            {L.util_pct}% used · {(100 - L.util_pct).toFixed(1)}% waste
                          </div>
                        )}
                        <NestingCanvas layout={L} thumbs={thumbsByKey} sectionInfo={lfSecLine(L, arr.length)} fit={nestFit} onFitChange={setNestFit} />
                      </div>
                    ))}
                    <PricingPanel r={sel.total} className="mt-3" onRush={setRushInfo} chips />
                    <div className="mt-3 flex justify-end">
                      <SaveQuoteBar module={cfg.saveModule || "Gran Formato"} title={`${sel.material.name} · ${res.mode}`} inputs={{ sizes, mode, laminate, transferTape, extras, machineId, inkCoverage, pageThumbs }} beforeSave={buildAndUploadMockup} summary={sel} />
                    </div>
                  </div>

                  <div>
                    <SectionLabel>Compare Materials</SectionLabel>
                    <div className="space-y-3">
                      {(() => {
                        const sorted = [...res.results].sort((a, b) => ((LF_SUBTYPES[subtypeKey(a.material)]?.order || 99) - (LF_SUBTYPES[subtypeKey(b.material)]?.order || 99)) || (leadNum(a.material.name) - leadNum(b.material.name)));
                        const cheapestId = [...res.results].sort((a, b) => (sell(a.total) ?? 1e12) - (sell(b.total) ?? 1e12))[0]?.material?.id;
                        const out = []; let cur = null; let bucket = [];
                        const flush = () => { if (bucket.length) { const g = LF_SUBTYPES[cur]; out.push(
                          <div key={cur}>
                            <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: g.color }}><span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: g.color }}></span>{g.label}</div>
                            <div className="grid sm:grid-cols-2 gap-3">{bucket}</div>
                          </div>); bucket = []; } };
                        sorted.forEach((r, idx) => {
                          const sk = subtypeKey(r.material);
                          if (sk !== cur) { flush(); cur = sk; }
                          const isSel = sel.material.id === r.material.id;
                          bucket.push(
                            <button key={r.material.id} data-testid="lf-compare-row" onClick={() => { setSel(r); setMaterialId(r.material.id); }}
                              className={`text-left rounded-xl border p-4 transition-all border-l-4 ${isSel ? "border-[#2495D3] ring-1 ring-[#2495D3]" : "border-slate-200 hover:border-slate-300"}`}
                              style={{ borderLeftColor: LF_SUBTYPES[sk].color }}>
                              <div className="flex items-center justify-between">
                                <div className="font-head font-bold text-sm flex items-center gap-1.5">
                                  {r.material.name}
                                  {r.material.is_default && <span className="text-[9px] font-mono uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="lf-compare-default-badge">Default</span>}
                                </div>
                                {r.material.id === cheapestId && <span className="text-[10px] font-mono uppercase bg-emerald-500 text-white px-2 py-0.5 rounded-full">Best Value</span>}
                              </div>
                              <div className="text-[11px] font-mono text-slate-400 mt-0.5">{num(r.material.printable_width, 0)}" print</div>
                              <div className="num text-xl font-black text-[#2495D3] mt-2">{money(sell(r.total))}</div>
                              {r.total.selling_price != null && r.total.wholesale_price != null && (
                                <div className="text-[11px] text-slate-500 num">WS {money(r.total.wholesale_price)}</div>
                              )}
                            </button>
                          );
                        });
                        flush();
                        return out;
                      })()}
                    </div>
                  </div>

                  <VolumePricingTable endpoint="/calc/largeformat"
                    makeBody={(q) => ({ sizes: [{ width: +sizes[0].width, height: +sizes[0].height, qty: q }], mode, laminate, transfer_tape: transferTape, extras: extras.map((e) => ({ type: e.type, material_id: e.material_id, width: +e.width || 0, height: +e.height || 0, qty: +e.qty || 1, die_cut: !!e.die_cut, transfer_tape: !!e.transfer_tape })), material_ids: typeMats.map((m) => m.id), machine_id: machineId !== "none" ? machineId : null, sticker: isSticker, designs: +designs || 1, ink_coverage_pct: inkCoverage })}
                    extract={(d) => ((d.results || []).find((r) => r.material.id === sel.material.id) || (d.results || [])[0])?.total}
                    signature={`${sizes[0]?.width}|${sizes[0]?.height}|${mode}|${laminate}|${transferTape}|${JSON.stringify(extras)}|${machineId}|${inkCoverage}|${sel.material.id}|${typeKey}`}
                    tiers={isSticker ? [25, 50, 100, 250, 500, 1000, 2500] : [1, 2, 3, 6, 12, 15, 24, 50]}
                    onPick={(q) => upd(0, "qty", q)}
                    activeQty={+sizes[0]?.qty}
                    moduleKey={isSticker ? "stickers" : "large-format"}
                    unitLabel="pc" />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="materials" className="mt-6">
            {isAdmin && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-500">Reference view · roll materials read from the central Materials DB (prices honor any overrides).</p>
                  <a href="/materials" className="text-xs text-[#2495D3] hover:underline" data-testid="manage-materials-link">Manage in Materials →</a>
                </div>
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        <th className="text-left px-4 py-2.5">Material</th>
                        <th className="text-left px-4 py-2.5">Finish</th>
                        <th className="text-right px-4 py-2.5">Printable width</th>
                        <th className="text-right px-4 py-2.5">$ / sqft</th>
                        <th className="text-right px-4 py-2.5">Retail</th>
                        <th className="text-right px-4 py-2.5">Wholesale</th>
                        <th className="text-center px-4 py-2.5">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...rollMats].sort(rollSort).map((m, idx, arr) => {
                        const sk = subtypeKey(m); const g = LF_SUBTYPES[sk];
                        const showHeader = idx === 0 || subtypeKey(arr[idx - 1]) !== sk;
                        return (
                          <Fragment key={m.id}>
                            {showHeader && (
                              <tr data-testid={`roll-group-${sk}`}><td colSpan={7} className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest bg-slate-50/70" style={{ color: g.color }}><span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: g.color }}></span>{g.label}</td></tr>
                            )}
                            <tr data-testid="roll-material-row" className="border-b border-slate-100 hover:bg-slate-50 border-l-4" style={{ borderLeftColor: g.color }}>
                              <td className="px-4 py-2.5">
                                <div className="font-medium flex items-center gap-2">
                                  {m.name}
                                  {m.is_default && <span className="inline-block text-[9px] font-mono uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="lf-roll-default-badge">Default</span>}
                                </div>
                                <div className="text-[11px] text-slate-400 font-mono">{m.code || "—"}</div>
                              </td>
                              <td className="px-4 py-2.5">
                                {m.finish ? <span data-testid="lf-roll-finish-badge" className="inline-block text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{m.finish}</span> : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-4 py-2.5 text-right num">{m.printable_width ? `${m.printable_width}"` : "—"}</td>
                              <td className="px-4 py-2.5 text-right num">{m.price_per_sqft != null ? money(m.price_per_sqft) : "—"}</td>
                              <td className="px-4 py-2.5 text-right num text-[#2495D3]">{m.selling_price != null ? money(m.selling_price) : "—"}</td>
                              <td className="px-4 py-2.5 text-right num text-slate-600">{m.wholesale_price != null ? money(m.wholesale_price) : "—"}</td>
                              <td className="px-4 py-2.5 text-center num">
                                <span className={m.low_stock ? "text-red-600 font-semibold" : ""}>{m.stock_qty != null ? Math.round(m.stock_qty) : "—"}</span>
                                {m.low_stock && <span data-testid="lf-roll-lowstock-badge" className="ml-2 inline-block text-[9px] font-mono uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">Low</span>}
                              </td>
                            </tr>
                          </Fragment>
                        );
                      })}
                      {rollMats.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No roll materials assigned. Add one in Materials.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="presets" className="mt-6">
            {isAdmin && <CrudManager endpoint="size-presets" fields={presetFields} columns={presetCols} prefix="preset" onChange={setPresets} />}
          </TabsContent>
          <TabsContent value="intel" className="mt-6">
            {isAdmin && <LFPriceIntel />}
          </TabsContent>
          <TabsContent value="misc" className="mt-6">
            <MiscListTab module={isSticker ? "stickers" : "large-format"} groups={isSticker ? null : LF_MISC_GROUPS} />
          </TabsContent>
        </Tabs>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6 cursor-zoom-out" onClick={() => setZoom(null)} data-testid="lf-thumb-zoom">
          <img src={zoom} alt="artwork preview" className="max-h-[88vh] max-w-[88vw] rounded-lg shadow-2xl bg-white" />
        </div>
      )}
      <CutFileDialog open={!!cutRow} onOpenChange={(o) => { if (!o) setCutRow(null); }} row={cutRow} isSticker={isSticker} />
      <BatchCutDialog open={batchOpen} onOpenChange={setBatchOpen} rows={sizes} isSticker={isSticker} />
    </div>
  );
}
